/**
 * アプリケーション認証フック
 * backend の session cookie を source of truth として認証状態を管理する
 */

import { authApi, type AuthSessionUser } from '@/lib/api/auth'
import { AUTH_UNAUTHORIZED_EVENT } from '@/lib/auth/authEvents'
import { recordAuthProbe, type AuthProbeTrigger } from '@/lib/auth/authObservability'
import type { UserRole } from '@/types/auth'
import { UserRoleValues } from '@/types/auth'
import { generateRequestId } from '@/utils/requestId'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import logger from '@/utils/logger'

/**
 * 待たせていることを利用者に伝えるまでの時間。
 * Cloud Run のコールドスタートは実測で23秒かかるので、ここでは打ち切らない（PRI-251）。
 */
const SLOW_NOTICE_MS = 8_000

/**
 * 認証確認を打ち切るまでの時間。
 * apiClient の30秒タイムアウトとは独立した保険で、通信が返ってこない経路でも
 * 画面が Loading… のまま残らないことを担保する（PRI-251）。
 */
const AUTH_TIMEOUT_MS = 45_000

const AUTH_TIMEOUT_MESSAGE =
  '認証の確認に時間がかかりすぎました。通信状況を確認して、もう一度お試しください。'

export type AppSession = {
  access_token?: string | null
}

export type AppUser = AuthSessionUser & {
  username: string | null
  user_metadata?: {
    name?: string | null
  }
}

interface SupabaseAuthContextType {
  session: AppSession | null
  user: AppUser | null
  isAdmin: boolean
  authError: string | null
  isLoading: boolean
  /** 認証確認が長引いていることを画面に伝えるためのフラグ */
  isAuthCheckSlow: boolean
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  /** 認証確認をやり直す。進行中のリクエストは中断する */
  retryAuthCheck: () => void
}

const SupabaseAuthContext = createContext<SupabaseAuthContextType | undefined>(undefined)

function normalizeRole(role: string | null | undefined): UserRole {
  const normalized = role?.toUpperCase()
  if (normalized === UserRoleValues.ADMIN) {
    return UserRoleValues.ADMIN
  }
  if (normalized === UserRoleValues.MANAGER) {
    return UserRoleValues.MANAGER
  }
  return UserRoleValues.USER
}

function buildAppUser(user: AuthSessionUser): AppUser {
  return {
    ...user,
    role: normalizeRole(user.role),
    username: user.email?.split('@')[0] ?? null,
    user_metadata: {
      name: user.name ?? null,
    },
  }
}

export const SupabaseAuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<AppSession | null>(null)
  const [user, setUser] = useState<AppUser | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthCheckSlow, setIsAuthCheckSlow] = useState(false)

  const isMountedRef = useRef(true)
  const userRef = useRef<AppUser | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const timersRef = useRef<number[]>([])
  // 何番目の認証確認かを覚えておき、追い越された分の結果は捨てる。
  // 中断した通信が遅れて成功しても、新しい認証状態を上書きさせない。
  const generationRef = useRef(0)
  const inFlightRequestIdRef = useRef<string | null>(null)

  useEffect(() => {
    userRef.current = user
  }, [user])

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id))
    timersRef.current = []
  }, [])

  const clearAuthState = useCallback(() => {
    setSession(null)
    setUser(null)
    setIsAdmin(false)
  }, [])

  const applyAuthenticatedState = useCallback((payload: AuthSessionUser) => {
    const nextUser = buildAppUser(payload)
    setSession({ access_token: null })
    setUser(nextUser)
    setIsAdmin(nextUser.role === UserRoleValues.ADMIN)
    setAuthError(null)
  }, [])

  const loadSession = useCallback(
    async (options?: { showLoading?: boolean; trigger?: AuthProbeTrigger }) => {
      // 進行中の認証確認があれば止めてから始める。
      // 開始したまま終わらない記録を残さないよう、追い越したことも書いておく
      if (inFlightRequestIdRef.current) {
        recordAuthProbe({ phase: 'superseded', requestId: inFlightRequestIdRef.current })
      }
      abortRef.current?.abort()
      clearTimers()

      const controller = new AbortController()
      abortRef.current = controller
      const generation = ++generationRef.current
      const isCurrent = () => generationRef.current === generation && isMountedRef.current

      const requestId = generateRequestId()
      const startedAt = Date.now()
      const elapsed = () => Date.now() - startedAt

      if (options?.showLoading) {
        setIsLoading(true)
      }
      setIsAuthCheckSlow(false)
      setAuthError(null)

      inFlightRequestIdRef.current = requestId
      recordAuthProbe({ phase: 'started', requestId, trigger: options?.trigger ?? 'initial' })

      timersRef.current.push(
        window.setTimeout(() => {
          if (!isCurrent()) {
            return
          }
          recordAuthProbe({ phase: 'slow', requestId, elapsedMs: elapsed() })
          setIsAuthCheckSlow(true)
        }, SLOW_NOTICE_MS),
      )

      timersRef.current.push(
        window.setTimeout(() => {
          if (generationRef.current !== generation) {
            return
          }
          controller.abort()
          inFlightRequestIdRef.current = null
          recordAuthProbe({ phase: 'timed-out', requestId, elapsedMs: elapsed() })
          if (!isMountedRef.current) {
            return
          }
          if (!userRef.current) {
            clearAuthState()
          }
          setAuthError(AUTH_TIMEOUT_MESSAGE)
          setIsAuthCheckSlow(false)
          setIsLoading(false)
        }, AUTH_TIMEOUT_MS),
      )

      try {
        const payload = await authApi.getSession({ signal: controller.signal, requestId })
        // 打ち切った直後に届いた結果は捨てる。画面はすでに再試行を出している
        if (controller.signal.aborted || !isCurrent()) {
          return
        }
        recordAuthProbe({ phase: 'succeeded', requestId, elapsedMs: elapsed() })
        applyAuthenticatedState(payload)
      } catch (error: unknown) {
        // 打ち切りと再試行による中断は、処理済みなのでここでは何もしない
        if (controller.signal.aborted || !isCurrent()) {
          return
        }

        // client.ts のインターセプタは AxiosError を ApiClientError に詰め替えて
        // reject するので、後者の error.status と、未詰め替えケースの error.response.status
        // の両方を確認する。
        const status = typeof error === 'object' && error
          ? ((error as { response?: { status?: number } }).response?.status
              ?? (error as { status?: number }).status)
          : undefined

        if (status === 401) {
          recordAuthProbe({ phase: 'unauthenticated', requestId, elapsedMs: elapsed() })
          clearAuthState()
          setAuthError(null)
          return
        }

        recordAuthProbe({
          phase: 'failed',
          requestId,
          elapsedMs: elapsed(),
          reason: status ? `http-${status}` : 'network',
        })
        logger.error('[Auth] Failed to resolve current session', error)
        if (!userRef.current) {
          clearAuthState()
        }
        setAuthError('権限情報の取得に失敗しました。再読み込みしてもう一度お試しください。')
      } finally {
        if (inFlightRequestIdRef.current === requestId) {
          inFlightRequestIdRef.current = null
        }
        if (generationRef.current === generation) {
          clearTimers()
        }
        if (isCurrent() && !controller.signal.aborted) {
          setIsAuthCheckSlow(false)
          setIsLoading(false)
        }
      }
    },
    [applyAuthenticatedState, clearAuthState, clearTimers],
  )

  useEffect(() => {
    isMountedRef.current = true
    void loadSession({ showLoading: true, trigger: 'initial' })

    const handleUnauthorized = () => {
      if (!isMountedRef.current) {
        return
      }
      clearAuthState()
      setAuthError(null)
      setIsAuthCheckSlow(false)
      setIsLoading(false)
    }

    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized)

    return () => {
      isMountedRef.current = false
      abortRef.current?.abort()
      clearTimers()
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized)
    }
  }, [clearAuthState, clearTimers, loadSession])

  const signOut = useCallback(async () => {
    setIsLoading(true)

    try {
      await authApi.logout()
    } catch (error) {
      logger.warn('[Auth] Logout request failed', {
        error: error instanceof Error ? error.message : String(error),
      })
    } finally {
      if (isMountedRef.current) {
        clearAuthState()
        setAuthError(null)
        setIsLoading(false)
      }
    }
  }, [clearAuthState])

  const refreshProfile = useCallback(async () => {
    await loadSession({ showLoading: true, trigger: 'refresh' })
  }, [loadSession])

  const retryAuthCheck = useCallback(() => {
    void loadSession({ showLoading: true, trigger: 'retry' })
  }, [loadSession])

  const value = useMemo<SupabaseAuthContextType>(
    () => ({
      session,
      user,
      isAdmin,
      authError,
      isLoading,
      isAuthCheckSlow,
      signOut,
      refreshProfile,
      retryAuthCheck,
    }),
    [
      authError,
      isAdmin,
      isAuthCheckSlow,
      isLoading,
      refreshProfile,
      retryAuthCheck,
      session,
      signOut,
      user,
    ],
  )

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  )
}

export const useSupabaseAuth = (): SupabaseAuthContextType => {
  const context = useContext(SupabaseAuthContext)
  if (context === undefined) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider')
  }
  return context
}
